import { getPointer, SpecialTagTypeEnum } from "libs/schema";
import { PermissionError } from "libs/errors";
import { ActionServerContext } from "../action";
import { ADMIN_USER_ID } from "../../../shared-constants";

function tagAccessError(params: { userId: string; tagId: string }) {
  return new PermissionError(`User ${params.userId} has no direct or transitive access to tag:${params.tagId}`);
}

async function innerAuthorizeUserHasAccessToTag(props: {
  tagId: string;
  context: ActionServerContext;
  accessType: "READ" | "WRITE";
}): Promise<boolean> {
  const {
    accessType,
    context: {
      currentUserId: userId,
      environment: { db, loader },
    },
    tagId,
  } = props;

  // We use the unauthorized tag record fetch to do our own logic but use existing read authorizations for
  // tag_user_member and tag_group_member records
  const [[tag], [tagUserMember], [tagGroupMembers]] = await Promise.all([
    db.getRecord(getPointer("tag", tagId), { includeSoftDeletes: false, currentUserId: ADMIN_USER_ID }),
    loader.getRecord(getPointer("tag_user_member", { tag_id: tagId, user_id: userId })),
    loader.getTagGroupMembers({ tag_id: tagId }),
  ]);

  if (tag?.type === SpecialTagTypeEnum.SINGLETON) {
    switch (accessType) {
      case "READ":
        return true;
      case "WRITE":
        throw new PermissionError("User can't directly update a singleton tag");
    }
  }

  // If there are entries in tagGroupMembers, it means the user has access to that group and that group is a member of
  // that tag
  return !!(tagUserMember || tagGroupMembers.length);
}

export async function authorizeUserHasAccessToTag(props: { tagId: string; context: ActionServerContext }) {
  const { tagId, context } = props;

  const hasAccess = await innerAuthorizeUserHasAccessToTag({
    tagId,
    accessType: "READ",
    context,
  });

  if (!hasAccess) {
    throw tagAccessError({ userId: context.currentUserId, tagId });
  }
}

export async function authorizeUserHasAccessToTags(props: { tagIds: string[]; context: ActionServerContext }) {
  const { tagIds, context } = props;

  await Promise.all(
    tagIds.map((tagId) =>
      innerAuthorizeUserHasAccessToTag({
        tagId,
        context,
        accessType: "READ",
      }).then((hasAccess) => {
        if (!hasAccess) {
          throw tagAccessError({ userId: context.currentUserId, tagId });
        }
      }),
    ),
  );
}

export async function authorizeUserCanUpdateTag(props: { tagId: string; context: ActionServerContext }) {
  const { tagId, context } = props;

  const hasAccess = await innerAuthorizeUserHasAccessToTag({
    tagId,
    accessType: "WRITE",
    context,
  });

  if (!hasAccess) {
    throw tagAccessError({ userId: context.currentUserId, tagId });
  }
}

export async function authorizeUserCanSubscribeToTag(props: { tagId: string; context: ActionServerContext }) {
  const { tagId, context } = props;

  const hasAccess = await innerAuthorizeUserHasAccessToTag({ tagId, context, accessType: "READ" });

  if (!hasAccess) {
    throw tagAccessError({ userId: context.currentUserId, tagId });
  }
}
